// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { alpha, styled } from '@mui/material/styles';
import * as React from 'react';
import { Button, IconButton, Menu, type MenuProps, Typography } from '../../../lib/base';
import { KeyboardArrowDownIcon, MoreHorizIcon } from '../../../lib/icons';

export const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 4,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

/**
Should look like this:

<MenuItem key="some-unique-key" onClick={handleClose} disableRipple>
  <EditIcon />
  Edit
</MenuItem>
 */
interface Props {
  readonly options: React.ReactElement[];
  readonly close: boolean;
  readonly useMoreIcon?: boolean;
  readonly id?: string;
  readonly useArrowOnly?: boolean;
}

export default function Options({ id, options, close, useMoreIcon, useArrowOnly }: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);
  React.useEffect(() => {
    if (close) {
      handleClose();
    }
  }, [close, handleClose]);

  return (
    <div id={id}>
      {useMoreIcon ? (
        <IconButton
          onClick={handleClick}
          id="options-button"
          aria-label="show-options"
          aria-controls={open ? 'options-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <MoreHorizIcon />
        </IconButton>
      ) : (
        <Button
          id="options-button"
          aria-controls={open ? 'options-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          variant="contained"
          onClick={handleClick}
          endIcon={!useArrowOnly && <KeyboardArrowDownIcon sx={{ color: 'secondary.contrastText' }} />}
          color="secondary"
        >
          {useArrowOnly ? (
            <KeyboardArrowDownIcon sx={{ color: 'secondary.contrastText' }} />
          ) : (
            <Typography variant="body1" sx={{ color: 'secondary.contrastText' }}>
              Options
            </Typography>
          )}
        </Button>
      )}
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          'aria-labelledby': 'options-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {options}
      </StyledMenu>
    </div>
  );
}
